// frontend/src/services/planosPagamentoService.js
import api from './api';

/**
 * Cria um novo plano de pagamento.
 * @param {object} planoPagamentoData - Dados do plano de pagamento.
 * @returns {object} - Resposta da API.
 */
export const criarPlanoPagamento = async (planoPagamentoData) => {
  try {
    const response = await api.post('/planos-pagamento', planoPagamentoData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao criar plano de pagamento');
  }
};
const criarPlanoIndividual = async (dados) => {
  try {
    const response = await api.post('/planos-pagamento/individual', dados);
    return response.data;
  } catch (error) {
    console.error('Erro ao criar plano individual:', error);
    throw error;
  }
};
/**
 * Lista todos os planos de pagamento (instituição).
 * @param {object} params - Parâmetros de filtro.
 * @returns {object} - Lista de planos de pagamento.
 */
export const listarPlanosPagamento = async (params = {}) => {
  try {
    const response = await api.get('/planos-pagamento', { params });
    if (!response.data) {
      throw new Error('Dados não recebidos da API');
    }
    return response.data;
  } catch (error) {
    console.error('Erro detalhado do serviço:', error);
    if (error.response) {
      throw new Error(error.response.data?.message || 'Erro ao listar planos de pagamento');
    }
    throw new Error('Erro de conexão com o servidor');
  }
};

/**
 * Lista planos de pagamento específicos do estudante.
 * @returns {object} - Lista de planos de pagamento do estudante.
 */
export const listarPlanosPagamentoEstudante = async () => {
  try {
    console.log('Iniciando busca de planos do estudante...');
    
    const response = await api.get('/planos-pagamento/estudante/meus-planos');
    
    console.log('Resposta recebida:', response.data);
    
    if (!response.data) {
      console.log('Nenhum dado recebido da API');
      throw new Error('Dados não recebidos da API');
    }

    // Verificar estrutura da resposta
    console.log('Total de planos:', response.data?.data?.length || 0);
    
    return response.data;
  } catch (error) {
    console.error('Erro ao listar planos:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw new Error(error.response?.data?.message || 'Erro ao listar planos de pagamento');
  }
};

/**
 * Obtém detalhes de um plano de pagamento.
 * @param {number} id - ID do plano de pagamento.
 * @returns {object} - Detalhes do plano de pagamento.
 */
export const obterPlanoPagamento = async (id) => {
  try {
    const response = await api.get(`/planos-pagamento/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao obter plano de pagamento');
  }
};

/**
 * Obtém detalhes completos do plano (com pagamentos e estudantes).
 * @param {number} id - ID do plano de pagamento.
 * @returns {object} - Detalhes completos do plano.
 */
export const obterDetalhesPlano = async (id) => {
  try {
    const response = await api.get(`/planos-pagamento/${id}/detalhes`);
    if (!response.data) {
      throw new Error('Dados não recebidos da API');
    }
    console.log('Detalhes do plano recebidos:', response.data);
    return response.data;
  } catch (error) {
    console.error('Erro ao obter detalhes do plano:', error);
    throw error;
  }
};

/**
 * Obtém detalhes de um estudante dentro de um plano específico.
 * @param {number} planoId - ID do plano de pagamento.
 * @param {number} estudanteId - ID do estudante.
 * @returns {object} - Detalhes do estudante no plano.
 */
export const obterDetalhesEstudante = async (planoId, estudanteId) => {
  try {
    const response = await api.get(`/planos-pagamento/${planoId}/estudante/${estudanteId}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao obter detalhes do estudante:', error);
    throw new Error(error.response?.data?.message || 'Erro ao obter detalhes do estudante');
  }
};

/**
 * Obtém pagamentos de um plano com filtros.
 * @param {number} id - ID do plano de pagamento.
 * @param {object} filtros - Filtros para a consulta de pagamentos.
 * @returns {object} - Lista de pagamentos do plano.
 */
export const obterPagamentosPlano = async (id, filtros = {}) => {
  try {
    const response = await api.get(`/planos-pagamento/${id}/pagamentos`, {
      params: filtros,
    });
    if (!response.data) {
      throw new Error('Dados não recebidos da API');
    }
    return response.data;
  } catch (error) {
    console.error('Erro ao obter pagamentos do plano:', error);
    throw new Error(error.response?.data?.message || 'Erro ao obter pagamentos do plano');
  }
};

/**
 * Obtém estatísticas financeiras do plano.
 * @param {number} id - ID do plano de pagamento.
 * @returns {object} - Estatísticas financeiras do plano.
 */
export const obterEstatisticasPlano = async (id) => {
  try {
    console.log('Iniciando chamada para obter estatísticas do plano:', id);
    const response = await api.get(`/planos-pagamento/${id}/estatisticas`);

    console.log('Resposta completa da API:', response);

    if (!response.data) {
      console.error('Dados não recebidos da API');
      throw new Error('Dados não recebidos da API');
    }

    // Verificar estrutura dos dados
    if (!response.data.estatisticas?.estudantes) {
      console.warn('Dados de estudantes não encontrados na resposta:', response.data);
    } else {
      console.log('Estudantes encontrados:', response.data.estatisticas.estudantes);
    }

    return response.data;
  } catch (error) {
    console.error('Erro detalhado ao obter estatísticas do plano:', {
      error,
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw new Error(error.response?.data?.message || 'Erro ao obter estatísticas do plano');
  }
};

/**
 * Obtém lista de estudantes associados ao plano.
 * @param {number} id - ID do plano de pagamento.
 * @returns {object} - Lista de estudantes no plano.
 */
export const obterEstudantesPlano = async (id) => {
  try {
    console.log('Iniciando busca de estudantes do plano:', id);
    const [estudantesResponse, estatisticasResponse] = await Promise.all([
      api.get(`/planos-pagamento/${id}/estudantes`),
      api.get(`/planos-pagamento/${id}/estatisticas`)
    ]);

    const estudantes = [];
    
    // Combinar estudantes de ambas as respostas
    if (estudantesResponse?.data?.data) {
      estudantes.push(...estudantesResponse.data.data);
    }
    
    if (estatisticasResponse?.data?.estatisticas?.estudantes) {
      const estudantesEstatisticas = estatisticasResponse.data.estatisticas.estudantes;
      estudantesEstatisticas.forEach(est => {
        if (!estudantes.find(e => e.id === est.id)) {
          estudantes.push(est);
        }
      });
    }

    console.log('Estudantes encontrados:', estudantes);
    
    return {
      success: true,
      data: estudantes
    };
  } catch (error) {
    console.error('Erro ao obter estudantes do plano:', error);
    throw error;
  }
};
/**
 * Exporta dados do plano em um formato específico (e.g., xlsx).
 * @param {number} id - ID do plano de pagamento.
 * @param {string} formato - Formato do arquivo (default: 'xlsx').
 */
export const exportarDadosPlano = async (id, formato = 'xlsx') => {
  try {
    const response = await api.get(`/planos-pagamento/${id}/exportar`, {
      params: { formato },
      responseType: 'blob',
    });

    // Criar URL para download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `plano-pagamento-${id}.${formato}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Erro ao exportar dados do plano:', error);
    throw new Error(error.response?.data?.message || 'Erro ao exportar dados do plano');
  }
};

/**
 * Lista planos base para seleção.
 * @returns {object} - Lista de planos base.
 */
export const listarPlanosBase = async () => {
  try {
    const response = await api.get('/planos-pagamento/base'); // URL correta
    if (!response.data) {
      throw new Error('Dados não recebidos da API');
    }
    return response.data;
  } catch (error) {
    console.error('Erro ao listar planos base:', error);
    throw new Error(error.response?.data?.message || 'Erro ao listar planos base');
  }
};

/**
 * Gera relatório financeiro do plano em PDF.
 * @param {number} id - ID do plano de pagamento.
 * @param {string} formato - Formato do relatório (default: 'pdf').
 */
export const gerarRelatorioFinanceiro = async (id, formato = 'pdf') => {
  try {
    const response = await api.get(`/planos-pagamento/${id}/relatorio`, {
      params: { formato },
      responseType: 'blob',
    });

    // Criar URL para download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `relatorio-financeiro-${id}.${formato}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Erro ao gerar relatório financeiro:', error);
    throw new Error(error.response?.data?.message || 'Erro ao gerar relatório financeiro');
  }
};

/**
 * Confirma múltiplos pagamentos em lote para um plano específico.
 * @param {number} id - ID do plano de pagamento.
 * @param {array} pagamentosIds - IDs dos pagamentos a serem confirmados.
 * @returns {object} - Resposta da API.
 */
export const confirmarPagamentosEmLote = async (id, pagamentosIds) => {
  try {
    const response = await api.post(`/planos-pagamento/${id}/confirmar-pagamentos`, {
      pagamentosIds,
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao confirmar pagamentos em lote:', error);
    throw new Error(error.response?.data?.message || 'Erro ao confirmar pagamentos em lote');
  }
};

/**
 * Atualiza um plano de pagamento existente.
 * @param {number} id - ID do plano de pagamento.
 * @param {object} planoPagamentoData - Dados atualizados do plano de pagamento.
 * @returns {object} - Resposta da API.
 */
export const atualizarPlanoPagamento = async (id, planoPagamentoData) => {
  try {
    const response = await api.put(`/planos-pagamento/${id}`, planoPagamentoData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao atualizar plano de pagamento');
  }
};

/**
 * Obtém o plano ativo de uma instituição.
 * @param {number} instituicaoId - ID da instituição.
 * @returns {object|null} - Plano ativo ou null se não encontrado.
 */
export const obterPlanoAtivo = async (instituicaoId) => {
  try {
    const response = await api.get('/planos-pagamento', {
      params: {
        instituicaoId,
        status: 'ATIVO',
      },
    });

    // Log para debug
    console.log('Resposta da busca de plano ativo:', response.data);

    // Verifica se há dados e retorna o primeiro plano ativo
    if (response.data?.data?.length > 0) {
      return response.data.data[0];
    }

    console.log('Nenhum plano ativo encontrado para a instituição:', instituicaoId);
    return null;
  } catch (error) {
    console.error('Erro ao obter plano ativo:', error);
    throw new Error('Não foi possível obter o plano ativo da instituição');
  }
};

/**
 * Exclui um plano de pagamento.
 * @param {number} id - ID do plano de pagamento.
 */
export const excluirPlanoPagamento = async (id) => {
  try {
    await api.delete(`/planos-pagamento/${id}`);
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao excluir plano de pagamento');
  }
};

// Função utilitária para formatar dados de exportação (se necessário)
const formatarDadosExportacao = (dados) => {
  // Implementar formatação específica se necessário
  return dados;
};

// Agrupa todos os serviços em um único objeto para exportação
const planosPagamentoService = {
  criarPlanoPagamento,
  criarPlanoIndividual,
  listarPlanosPagamento,
  listarPlanosPagamentoEstudante,
  obterPlanoPagamento,
  obterDetalhesPlano,
  obterPagamentosPlano,
  obterEstatisticasPlano,
  obterEstudantesPlano,
  exportarDadosPlano,
  listarPlanosBase,
  obterDetalhesEstudante,
  gerarRelatorioFinanceiro,
  confirmarPagamentosEmLote,
  atualizarPlanoPagamento,
  obterPlanoAtivo,
  excluirPlanoPagamento,
  formatarDadosExportacao,
};

export default planosPagamentoService;
